import googleLogo from '@cannect/assets/svg/google-logo.svg'
import { Typography } from '@cannect/new-components/atoms'
import { testimonialsData } from '@cannect/new-components/Testimonials/testimonialsData'
import { useGetGoogleReviews } from '@cannect/services/resources/reviews'
import { LuStar } from 'react-icons/lu'

const MAX_REVIEWS = 3
const FALLBACK_RATING = 5

type TReviewCard = {
  id: string
  name: string
  /** Linha secundária sob o nome: idade nos dados estáticos, tempo relativo nos do Google. */
  secondary?: string
  quote: string
  rating: number
  photo?: string
}

/**
 * Estrelas. `warning-400` nos dois tamanhos: o design usa `warning-200` (#FEDE84) nos cards, que
 * rende 1,31:1 sobre branco e fica quase invisível. Amarelo de rating é sempre baixo em contraste
 * por convenção de mercado, então a correção certa não é repintar mas garantir o texto alternativo
 * — daí o `role="img"` com `aria-label`, que expõe a nota independentemente do visual.
 */
const StarRating = ({ rating, size }: { rating: number; size: 'sm' | 'lg' }) => {
  const filled = Math.round(rating)
  return (
    <span
      role="img"
      aria-label={`${rating.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} de 5 estrelas`}
      className={`flex shrink-0 items-center ${size === 'lg' ? 'gap-1' : 'gap-2'}`}>
      {Array.from({ length: 5 }, (_, index) => (
        <LuStar
          key={index}
          aria-hidden
          fill="currentColor"
          className={`${size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} ${
            index < filled ? 'text-warning-400' : 'text-neutral-200'
          }`}
        />
      ))}
    </span>
  )
}

const ReviewSkeleton = () => (
  <li className="flex animate-pulse flex-col gap-[28px] rounded-[8px] bg-white px-6 py-8">
    <div className="flex items-center gap-2">
      <div className="size-14 shrink-0 rounded-full bg-neutral-100" />
      <div className="flex flex-col gap-2">
        <div className="h-4 w-32 rounded bg-neutral-100" />
        <div className="h-3 w-20 rounded bg-neutral-100" />
      </div>
    </div>
    <div className="flex flex-col gap-2">
      <div className="h-3 w-full rounded bg-neutral-100" />
      <div className="h-3 w-full rounded bg-neutral-100" />
      <div className="h-3 w-3/4 rounded bg-neutral-100" />
    </div>
    <div className="h-5 w-[132px] rounded bg-neutral-100" />
  </li>
)

/**
 * Seção "O que dizem nossos pacientes" da home (Figma node 4161:29795).
 *
 * Consome as avaliações reais do Google (`/google-reviews/five-stars`) e cai para os depoimentos
 * estáticos se a chamada falhar. A nota agregada do cabeçalho vem de `business.overallRating`.
 *
 * Uma lacuna de dados: o design mostra a idade do paciente ("38 anos") sob o nome, mas a API do
 * Google não expõe idade — o payload de review tem apenas autor, foto, nota, texto e data. Nessa
 * linha entra o tempo relativo da avaliação ("há 2 meses"), que é dado real e ocupa a mesma posição;
 * quando o fallback estático é usado, a idade aparece porque aquele dataset a tem.
 *
 * Componente novo em vez de restyle de `Testimonials`: aquele também serve `ProductDetailsV2`, como
 * faixa com scroll horizontal e sem cabeçalho de seção, e herdaria o cabeçalho da home.
 */
export const PatientReviews = () => {
  const { data, isLoading, isError } = useGetGoogleReviews()

  const googleReviews = data?.fiveStarReviews?.reviews ?? []
  const useGoogle = !isError && googleReviews.length > 0

  const reviews: TReviewCard[] = useGoogle
    ? googleReviews.slice(0, MAX_REVIEWS).map((review, index) => ({
        id: `${review.author}-${review.time ?? index}`,
        name: review.author,
        secondary: review.relativeTime,
        quote: review.text,
        rating: review.rating ?? FALLBACK_RATING,
        photo: review.authorPhoto
      }))
    : testimonialsData.slice(0, MAX_REVIEWS).map((item) => ({
        id: item.name,
        name: item.name,
        secondary: item.age > 0 ? `${item.age} anos` : undefined,
        quote: item.quote,
        rating: FALLBACK_RATING,
        photo: item.image
      }))

  const overallRating = data?.business?.overallRating ?? FALLBACK_RATING

  return (
    <section aria-labelledby="patient-reviews-heading" className="w-full bg-neutral-50">
      <div className="mx-auto flex w-full max-w-[1230px] flex-col gap-16 px-8 py-16">
        {/* Cabeçalho centralizado (node 4333:1294). Aqui o H2 é 28px, não os 36px do blog e do
            aplicativo — é a medida do arquivo para esta seção. */}
        <div className="flex w-full flex-col items-center gap-2 text-center">
          <p className="m-0 text-base leading-[1.6] text-neutral-800">O que dizem nossos pacientes</p>

          <div className="flex flex-col items-center gap-4">
            <Typography.Heading
              id="patient-reviews-heading"
              level={2}
              weight="regular"
              className="m-0 text-[28px] leading-[1.2] tracking-[-0.448px] text-neutral-800">
              {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica. */}
              Resultados que <span className="italic">falam</span> por si
            </Typography.Heading>

            <div className="flex flex-wrap items-center justify-center gap-2">
              <img src={googleLogo} alt="Google" className="size-6 shrink-0 rounded-[4px]" />
              <StarRating rating={overallRating} size="lg" />
              <p className="m-0 text-base leading-[1.6] text-neutral-800">
                {overallRating.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} de
                satisfação
              </p>
            </div>
          </div>
        </div>

        <ul className="m-0 grid list-none grid-cols-1 items-stretch gap-5 p-0 md:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: MAX_REVIEWS }, (_, index) => <ReviewSkeleton key={index} />)
            : reviews.map((review) => (
                <li key={review.id} className="flex flex-col gap-[28px] rounded-[8px] bg-white px-6 py-8">
                  <div className="flex items-center gap-2">
                    {review.photo && (
                      <img
                        src={review.photo}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="size-14 shrink-0 rounded-full object-cover"
                      />
                    )}
                    <div className="flex min-w-0 flex-col">
                      <p className="m-0 break-words text-lg font-semibold leading-[1.4] text-secondary-1000">
                        {review.name}
                      </p>
                      {review.secondary && (
                        <p className="m-0 text-sm leading-[1.4] text-secondary-1000">{review.secondary}</p>
                      )}
                    </div>
                  </div>

                  <p className="m-0 font-ebGaramond text-lg leading-[1.4] text-neutral-800">“{review.quote}”</p>

                  <StarRating rating={review.rating} size="sm" />
                </li>
              ))}
        </ul>
      </div>
    </section>
  )
}
